import { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginForm from './components/auth/LoginForm';
import SignupForm from './components/auth/SignupForm';
import AppShell from './components/layout/AppShell';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import IdeaVault from './pages/IdeaVault';
import MeetingNotes from './pages/MeetingNotes';
import CompanyMatching from './pages/CompanyMatching';
import TrendIntelligence from './pages/TrendIntelligence';
import BomBuilder from './pages/BomBuilder';
import PitchBuilder from './pages/PitchBuilder';
import IRPipeline from './pages/IRPipeline';
import EventOrganizer from './pages/EventOrganizer';
import SubmissionForm from './components/event/SubmissionForm';
import PlaceholderPage from './pages/PlaceholderPage';
import EventBuilder from './pages/EventBuilder';
import EventPublicPage from './pages/public/EventPublicPage';
import EventCompanyPage from './pages/public/EventCompanyPage';
import EventInventorPage from './pages/public/EventInventorPage';
import PendingApproval from './pages/PendingApproval';
import AdminDashboard from './pages/AdminDashboard';
import DemoInsights from './pages/DemoInsights';
import MyInventors from './pages/MyInventors';
import CompaniesPage from './pages/CompaniesPage';
import CompanyEventDashboard from './pages/CompanyEventDashboard';
import InventorEventDashboard from './pages/InventorEventDashboard';
import EventLandingPage from './pages/EventLandingPage';
import OrgTeam from './pages/OrgTeam';
import WishListBuilder from './pages/WishListBuilder';
import CompanySettings from './pages/CompanySettings';
import IdeaPipeline from './pages/IdeaPipeline';
import TermsOfService from './pages/public/TermsOfService';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import LandingPage from './pages/public/LandingPage';
import MarketIntelligence from './pages/MarketIntelligence';

// [EVENT-MODE] Holding screen shown when the event hasn't started or has ended
function EventWindowScreen({ eventSettings, eventEnded }) {
  const eventName = eventSettings?.event_name || 'PlayVentr Pitch Event';
  const startDate = eventSettings?.event_start
    ? new Date(eventSettings.event_start).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;
  const endDate = eventSettings?.event_end
    ? new Date(eventSettings.event_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: '0 24px' }}>
        <img src="/logo-playventr.png" alt="PlayVentr" style={{ height: 48, margin: '0 auto 24px', display: 'block' }} />
        <h1 style={{ fontFamily: '"EB Garamond", Georgia, serif', fontSize: 28, color: '#0F172A', marginBottom: 12 }}>
          {eventEnded ? 'This Event Has Ended' : 'Event Coming Soon'}
        </h1>
        <p style={{ fontSize: 15, color: '#64748B', lineHeight: 1.6, marginBottom: 16 }}>
          {eventEnded
            ? `${eventName} has concluded. Thank you for participating!`
            : `${eventName} hasn't started yet.`}
        </p>
        {!eventEnded && startDate && (
          <p style={{ fontSize: 14, color: '#c9a84c', fontWeight: 600 }}>
            Event starts: {startDate}
          </p>
        )}
        {eventEnded && endDate && (
          <p style={{ fontSize: 13, color: '#94A3B8', marginTop: 8 }}>
            Event ended: {endDate}
          </p>
        )}
      </div>
    </div>
  );
}

function AppContent() {
  const {
    user, profile, loading, role,
    hasSubscription, hasEventAccess, activeEventRegistration,
    eventSettings, eventStarted, eventEnded,
    refreshProfile,
  } = useAuth();

  const [page, setPage] = useState('dashboard');
  const [authView, setAuthView] = useState('landing');
  const [pitchContext, setPitchContext] = useState(null);
  const [eventBuilderCtx, setEventBuilderCtx] = useState(null);
  const [activeEventId, setActiveEventId] = useState(null);

  // Public routes (no auth required)
  const path = window.location.pathname;
  if (path === '/terms') return <TermsOfService />;
  if (path === '/privacy') return <PrivacyPolicy />;

  const eventSubmitMatch = path.match(/^\/event\/([^/]+)\/submit$/);
  if (eventSubmitMatch) return <SubmissionForm eventId={eventSubmitMatch[1]} />;

  const eventCompanyMatch = path.match(/^\/event\/([^/]+)\/companies$/);
  if (eventCompanyMatch) return <EventCompanyPage slug={eventCompanyMatch[1]} />;

  const eventInventorMatch = path.match(/^\/event\/([^/]+)\/inventors$/);
  if (eventInventorMatch) return <EventInventorPage slug={eventInventorMatch[1]} />;

  const eventPublicMatch = path.match(/^\/event\/([^/]+)$/);
  if (eventPublicMatch) return <EventPublicPage slug={eventPublicMatch[1]} />;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <img src="/logo-playventr.png" alt="PlayVentr" style={{ height: 48, margin: '0 auto 16px', display: 'block' }} />
          <p style={{ fontSize: 13, color: '#94A3B8', marginTop: 8 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    if (authView === 'login') return <LoginForm onSwitch={() => setAuthView('signup')} />;
    if (authView === 'signup') return <SignupForm onSwitch={() => setAuthView('login')} />;
    return <LandingPage onSignIn={() => setAuthView('login')} />;
  }

  // Pending company approval
  if (role === 'pending_company') return <PendingApproval />;

  // [EVENT-MODE] Non-admin users see holding screen if event window is not active
  if (role !== 'admin' && eventSettings) {
    if (eventEnded) {
      return <EventWindowScreen eventSettings={eventSettings} eventEnded={true} />;
    }
    if (!eventStarted) {
      return <EventWindowScreen eventSettings={eventSettings} eventEnded={false} />;
    }
  }

  // [EVENT-MODE] Non-admin users without access see a "waiting for access" screen
  if (role !== 'admin' && !hasSubscription) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Poppins, sans-serif' }}>
        <div style={{ textAlign: 'center', maxWidth: 480, padding: '0 24px' }}>
          <img src="/logo-playventr.png" alt="PlayVentr" style={{ height: 48, margin: '0 auto 24px', display: 'block' }} />
          <h1 style={{ fontFamily: '"EB Garamond", Georgia, serif', fontSize: 28, color: '#0F172A', marginBottom: 12 }}>
            Access Pending
          </h1>
          <p style={{ fontSize: 15, color: '#64748B', lineHeight: 1.6 }}>
            Your account has been created. An admin will grant you access shortly.
          </p>
          <p style={{ fontSize: 13, color: '#94A3B8', marginTop: 16 }}>
            Signed in as {profile?.email}
          </p>
          <button
            onClick={async () => {
              await refreshProfile();
            }}
            style={{ marginTop: 16, padding: '10px 24px', borderRadius: 12, border: '2px solid #E2E8F0', background: 'white', color: '#0F172A', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Check Again
          </button>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    switch (page) {
      case 'dashboard':
        return <Dashboard onNavigate={setPage} />;

      // [EVENT-MODE] No subscription gating — all features available to granted users
      case 'vault':
        return <IdeaVault />;

      case 'matching':
        return <CompanyMatching onBuildPitch={(ideaId, companyId) => {
          setPitchContext({ ideaId, companyId });
          setPage('pitch');
        }} />;

      case 'trends':
        return <TrendIntelligence onApplyToPitch={(ideaId, trendId) => {
          setPitchContext({ ideaId, trendId });
          setPage('pitch');
        }} />;

      case 'bom':
        return <BomBuilder />;

      case 'pitch':
        return <PitchBuilder initialContext={pitchContext} onContextConsumed={() => setPitchContext(null)} />;

      case 'notes':
        return <MeetingNotes onNavigate={setPage} />;

      case 'market-intelligence':
        if (role === 'company' || role === 'admin') {
          return <MarketIntelligence />;
        }
        return <Dashboard onNavigate={setPage} />;

      case 'ir-pipeline':
        return <IRPipeline />;

      case 'idea-pipeline':
        return <IdeaPipeline />;

      case 'my-inventors':
        return <MyInventors />;

      case 'companies':
        return <CompaniesPage />;

      case 'wishlist':
        if (role === 'company' || role === 'admin') return <WishListBuilder />;
        return <Dashboard onNavigate={setPage} />;

      case 'events': {
        if (role === 'inventor') {
          if (hasEventAccess && activeEventRegistration) {
            return <InventorEventDashboard
              eventId={activeEventRegistration.event_id}
              onBack={() => setPage('dashboard')}
              onNavigate={setPage}
            />;
          }
          return <EventLandingPage role="inventor" hasSubscription={hasSubscription} onNavigate={setPage} />;
        }
        if (role === 'company') {
          if (hasEventAccess && activeEventRegistration) {
            return <CompanyEventDashboard
              eventId={activeEventRegistration.event_id}
              onBack={() => setPage('dashboard')}
            />;
          }
          return <EventLandingPage role="company" hasSubscription={hasSubscription} onNavigate={setPage} />;
        }
        return <EventOrganizer
          onCreateEvent={(id) => { setEventBuilderCtx({ pitchEventId: id }); setPage('event-builder'); }}
          onEditEventHomepage={(id) => { setEventBuilderCtx({ editId: id }); setPage('event-builder'); }}
        />;
      }

      case 'event-builder':
        return <EventBuilder
          eventId={eventBuilderCtx?.editId}
          pitchEventId={eventBuilderCtx?.pitchEventId}
          onBack={() => { setEventBuilderCtx(null); setPage('events'); }}
        />;

      case 'company-event-dashboard':
        return <CompanyEventDashboard
          eventId={activeEventId}
          onBack={() => { setActiveEventId(null); setPage('events'); }}
        />;

      case 'inventor-event-dashboard':
        return <InventorEventDashboard
          eventId={activeEventId}
          onBack={() => { setActiveEventId(null); setPage('events'); }}
          onNavigate={setPage}
        />;

      case 'admin-dashboard':
        if (role !== 'admin') return <Dashboard onNavigate={setPage} />;
        return <AdminDashboard />;

      case 'demo-insights':
        if (role !== 'admin') return <Dashboard onNavigate={setPage} />;
        return <DemoInsights />;

      case 'royalty':
        return <PlaceholderPage title="Royalty Calculator" description="Project royalty income across conservative, moderate, and optimistic scenarios." />;
      case 'calendar':
        return <PlaceholderPage title="PD Season Calendar" description="Product development season timelines and deadline alerts." />;
      case 'factory':
        return <PlaceholderPage title="Factory Research" description="Import Yeti integration for factory sourcing based on BOM and product category." />;

      case 'company-settings':
        if (role === 'company' || role === 'admin') return <CompanySettings onNavigate={setPage} />;
        return <Dashboard onNavigate={setPage} />;

      case 'team':
        if (role === 'company') return <OrgTeam />;
        return <Dashboard onNavigate={setPage} />;

      case 'settings':
        return <Settings />;

      default:
        return <Dashboard onNavigate={setPage} />;
    }
  };

  return (
    <AppShell currentPage={page} onNavigate={setPage}>
      {renderPage()}
    </AppShell>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
